import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { notesApi } from '../../services/cloudApi';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

/**
 * Sahip notu silmek istediğinde editör yoksa silemez; editör varsa sahipliği devretmek zorunda.
 * Bu modal sahipliği devretmek için bir editör seçtirir.
 */
const TransferOwnershipModal = ({ isOpen, onClose, note, onTransferred }) => {
  const { t } = useTranslation('notes');
  const { authToken } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const editors = (note?.collaborators || []).filter((c) => c.role === 'editor');

  const handleTransfer = async () => {
    if (!note?.id || !selectedUserId || !authToken) return;
    setError(null);
    setLoading(true);
    try {
      await notesApi.transferNoteOwnership({
        token: authToken,
        noteId: note.id,
        newOwnerUserId: selectedUserId,
      });
      if (typeof onTransferred === 'function') {
        onTransferred();
      }
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: { type: 'success', title: t('transferOwnership.successTitle'), message: t('transferOwnership.successMessage') },
        })
      );
      window.dispatchEvent(new CustomEvent('notesplus-reload-notes'));
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, { fallback: t('transferOwnership.errorFallback') }));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[100] bg-slate-900/30 backdrop-blur-sm flex items-center justify-center"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="fixed inset-0 flex items-center justify-center p-4 z-[10001]"
        onClick={onClose}
      >
        <div
          className="bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-md border border-white/10 dark:border-white/10 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-white/10 dark:border-white/10">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white">
              {t('transferOwnership.title')}
            </h3>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4">
            <p className="text-sm text-[var(--color-text-secondary)] dark:text-[var(--color-text-muted)] mb-4">
              {t('transferOwnership.description')}
            </p>
            {editors.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]">{t('transferOwnership.noEditors')}</p>
            ) : (
              <div className="space-y-2">
                {editors.map((c) => {
                  const name = c.name || c.email || t('transferOwnership.unknownUser');
                  const isSelected = selectedUserId === c.user_id;
                  return (
                    <button
                      key={c.user_id}
                      type="button"
                      onClick={() => setSelectedUserId(c.user_id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-colors ${
                        isSelected
                          ? 'border-[#3d8ef0]/50 bg-primary-50 dark:bg-[#2563eb]/10/20'
                          : 'border-transparent bg-[var(--color-bg-muted)] dark:bg-[#161f31] hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47]'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-full bg-[#2563eb]/10 dark:bg-[#2563eb]/10 flex items-center justify-center flex-shrink-0">
                        {c.avatar_url ? (
                          <img src={c.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-[#3d8ef0] dark:text-[#7ab8f8]" />
                        )}
                      </div>
                      <span className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white truncate">
                        {name}
                      </span>
                      {isSelected && <Crown className="w-4 h-4 text-amber-500 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}
            {error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
          </div>
          <div className="p-4 border-t border-white/10 dark:border-white/10 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] rounded-lg"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleTransfer}
              disabled={!selectedUserId || loading}
              className="px-4 py-2 text-sm font-medium bg-[#2563eb] hover:bg-[#3d8ef0] text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : null}
              {t('transferOwnership.transfer')}
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TransferOwnershipModal;
